import { loadStripe, Stripe } from "@stripe/stripe-js";

import { getErrorMessage } from "./error_message";

// Matches the transport-retry backoff used for data fetches: two short waits so
// a recovered load still lands inside the window a reader takes for "loading"
// rather than "broken". Nothing here waits on a merely slow script — a blocked
// one fails at once — so spacing the attempts wider only delays the message.
const RETRY_BACKOFF_MS = [250, 750];

// A filtering proxy or captive portal can accept the request and then never
// answer, firing neither `load` nor `error`. Without a ceiling that load stays
// pending forever and the surface it gates never resolves either way, so the
// wait is bounded and a non-answer is treated as the failure it is.
const LOAD_DEADLINE_MS = 10_000;

// The two ways the script itself fails to arrive: the `error` event on the
// injected tag, which Stripe's own loader words for us, and the deadline above.
const BLOCKED_MESSAGE = "Failed to load Stripe.js";
const TIMEOUT_MESSAGE = "Timed out loading Stripe.js";

/**
 * Why a bounded load gave up.
 *
 * `blocked` and `timeout` are the reader's environment — an extension, a
 * filtering network — and nothing here fixes either. `unexpected` is everything
 * else the load can reject with, and those are ours: an unset or malformed
 * publishable key makes Stripe's constructor throw, and a script that loads
 * without defining `Stripe` rejects too. Keeping the three apart stops a config
 * regression we must fix from being filed under noise we have decided not to.
 */
export type StripeJsFailure = "blocked" | "timeout" | "unexpected";

/**
 * Whether recovering from this failure needs a fresh document.
 *
 * Stripe's loader memoizes its in-flight promise module-wide and clears that
 * cache only when the promise rejects. A load abandoned at our deadline never
 * rejected — the request is still outstanding inside the loader — so calling it
 * again returns the same pending promise and waits on the same hung request
 * rather than injecting a new script. Nothing in the module's API resets that,
 * so only a new document can. A blocked script did reject, clearing the cache,
 * and retries in place.
 *
 * This is about a retry the reader asks for. The abandoned request can still
 * answer on its own, and `onLateLoad` takes that arrival without a reload.
 */
export function needsFreshDocumentToRetry(failure: StripeJsFailure): boolean {
  return failure === "timeout";
}

export function classifyStripeJsFailure(error: unknown): StripeJsFailure {
  // Matched whole rather than as a substring, so a longer message that happens
  // to quote one of these does not inherit its classification.
  const message = getErrorMessage(error).trim().toLowerCase();
  if (message === TIMEOUT_MESSAGE.toLowerCase()) {
    return "timeout";
  }
  if (message === BLOCKED_MESSAGE.toLowerCase()) {
    return "blocked";
  }
  return "unexpected";
}

type StripeLoader = (publishableKey: string) => Promise<Stripe | null>;

interface LoadStripeWithRetryOptions {
  load?: StripeLoader;
  backoffMs?: readonly number[];
  deadlineMs?: number;
  /**
   * A load abandoned at the deadline arrived after all, the caller's rejection
   * having already been delivered. Called at most once, and never for a load
   * that beat the deadline. A caller that has since moved on — unmounted, or
   * switched publishable key — has to ignore it.
   */
  onLateLoad?: (stripe: Stripe | null) => void;
}

/**
 * `loadStripe` with bounded retries and a bounded wait.
 *
 * Stripe.js is fetched from a third-party origin, so an ad blocker, a privacy
 * extension, a filtering proxy or a dropped connection all reject the load —
 * and the rejection looks identical whether the cause is momentary or
 * permanent. Retrying is safe and worthwhile because Stripe's own loader
 * discards its cached promise whenever an attempt fails and re-injects the
 * script tag on the next call, so calling it again is a genuine second attempt
 * rather than a replay of the first failure.
 *
 * The deadline ends the caller's wait, not the load. Nothing here can cancel an
 * injected script, so the request stays outstanding regardless, and abandoning
 * its result as well would turn a merely slow load into a permanent failure
 * that only a fresh document clears. It is watched to the end instead, and a
 * late arrival is handed back through `onLateLoad`.
 */
export function loadStripeWithRetry(
  publishableKey: string,
  options: LoadStripeWithRetryOptions = {}
): Promise<Stripe | null> {
  const {
    load = loadStripe,
    backoffMs = RETRY_BACKOFF_MS,
    deadlineMs = LOAD_DEADLINE_MS,
    onLateLoad,
  } = options;

  let deadline: ReturnType<typeof setTimeout> | undefined;

  const loading = retryLoad(load, publishableKey, backoffMs);

  return Promise.race([
    loading,
    new Promise<never>((_resolve, reject) => {
      deadline = setTimeout(() => {
        // Reaching this means the load has not settled — one that beat the
        // deadline disarmed the timer on its way out — so anything it does
        // from here is by definition late, and subscribing only now saves
        // asking a flag about it afterwards. Nothing is done with either
        // outcome of the derived promise — a late failure repeats the one
        // rejected just below — but it still has to be settled here, or that
        // failure, and anything the callback itself throws, surfaces as an
        // unhandled rejection.
        loading.then(onLateLoad).catch(() => {});
        reject(new Error(TIMEOUT_MESSAGE));
      }, deadlineMs);
    }),
  ]).finally(() => {
    // A load that wins the race would otherwise leave the deadline armed, and
    // every provider remount arms another.
    clearTimeout(deadline);
  });
}

// Only a rejection advances this loop, so a load that hangs rather than fails
// stays on its first attempt and the backoff never runs — the deadline is the
// whole answer to a hang. Bounding each attempt separately would not change
// that: the loader hands its memoised pending promise to every later call, so
// each one waits on the same unanswered request.
async function retryLoad(
  load: StripeLoader,
  publishableKey: string,
  backoffMs: readonly number[]
): Promise<Stripe | null> {
  for (const waitMs of backoffMs) {
    try {
      return await load(publishableKey);
    } catch {
      await delay(waitMs);
    }
  }

  // The final attempt is unguarded so a genuine failure reaches the caller with
  // its original error.
  return load(publishableKey);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
